import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, Check, X, FileText, Camera, User, MapPin } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import '../../App.css';

interface GuarantorRequest {
  request_id: string;
  guarantor_name: string;
  guarantor_email: string;
  guarantor_phone: string;
  status: string;
  application_reference: string;
  product_name: string;
  amount_requested: number;
  applicant_name: string;
  expires_at: string;
  guarantor_address?: string;
  guarantor_city?: string;
  guarantor_state?: string;
  guarantor_id_type?: string;
}

interface Document {
  document_type: string;
  file_name: string;
  uploaded_at: string;
}

const GuarantorAuthorization: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [request, setRequest] = useState<GuarantorRequest | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);

  // Form state
  const [step, setStep] = useState<'info' | 'documents' | 'review' | 'success'>('info');
  const [formData, setFormData] = useState({
    address: '',
    city: '',
    state: '',
    idType: '',
  });

  // Document uploads
  const [idDocument, setIdDocument] = useState<File | null>(null);
  const [selfie, setSelfie] = useState<File | null>(null);
  const [idDocumentUploaded, setIdDocumentUploaded] = useState(false);
  const [selfieUploaded, setSelfieUploaded] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5030/api';

  // Fetch guarantor request details
  useEffect(() => {
    const fetchRequest = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/guarantor/request/${token}`);
        const data = await response.json();

        if (!data.success) {
          setError(data.message || 'Failed to load guarantor request');
          return;
        }

        setRequest(data.data.request);
        setDocuments(data.data.documents || []);

        // Pre-fill form if data exists
        if (data.data.request.guarantor_address) {
          setFormData({
            address: data.data.request.guarantor_address || '',
            city: data.data.request.guarantor_city || '',
            state: data.data.request.guarantor_state || '',
            idType: data.data.request.guarantor_id_type || '',
          });
        }

        // Check which documents are uploaded
        const uploadedTypes = data.data.documents.map((doc: Document) => doc.document_type);
        setIdDocumentUploaded(uploadedTypes.includes('id_document'));
        setSelfieUploaded(uploadedTypes.includes('selfie'));

        // If already submitted, go to success step
        if (data.data.request.status === 'submitted') {
          setStep('success');
        }
      } catch (err: any) {
        setError('Failed to load guarantor request. Please try again.');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchRequest();
    }
  }, [token, API_URL]);

  // Update guarantor information
  const handleSubmitInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.address || !formData.city || !formData.state || !formData.idType) {
      setError('Please fill in all fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/guarantor/request/${token}/info`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: formData.address,
          city: formData.city,
          state: formData.state,
          idType: formData.idType,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        setError(data.message || 'Failed to update information');
        return;
      }

      setStep('documents');
    } catch (err: any) {
      setError('Failed to update information. Please try again.');
      console.error('Update error:', err);
    }
  };

  // Upload document
  const handleUploadDocument = async (documentType: 'id_document' | 'selfie', file: File) => {
    setError('');

    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('documentType', documentType);

      const response = await fetch(`${API_URL}/guarantor/request/${token}/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!data.success) {
        setError(data.message || 'Failed to upload document');
        return;
      }

      if (documentType === 'id_document') {
        setIdDocumentUploaded(true);
      } else if (documentType === 'selfie') {
        setSelfieUploaded(true);
      }

      setSuccess(`${documentType === 'id_document' ? 'ID Document' : 'Selfie'} uploaded successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Failed to upload document. Please try again.');
      console.error('Upload error:', err);
    }
  };

  // Submit guarantor request
  const handleFinalSubmit = async () => {
    setError('');

    if (!idDocumentUploaded || !selfieUploaded) {
      setError('Please upload all required documents before submitting');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/guarantor/request/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        setError(data.message || 'Failed to submit request');
        return;
      }

      setStep('success');
    } catch (err: any) {
      setError('Failed to submit request. Please try again.');
      console.error('Submit error:', err);
    }
  };

  if (loading) {
    return (
      <div className="public-layout">
        <div className="container" style={{ textAlign: 'center', padding: '4rem 1rem' }}>
          <div className="spinner"></div>
          <p>Loading guarantor request...</p>
        </div>
      </div>
    );
  }

  if (error && !request) {
    return (
      <div className="public-layout">
        <div className="container" style={{ maxWidth: '600px', margin: '4rem auto', padding: '2rem' }}>
          <div className="alert alert-error">
            <X size={20} />
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!request) {
    return null;
  }

  return (
    <div className="public-layout">
      <div className="container" style={{ maxWidth: '800px', margin: '2rem auto', padding: '1rem' }}>
        {/* Header */}
        <div className="guarantor-header" style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1>Guarantor Authorization</h1>
          <p className="text-muted">Complete the authorization process to support the loan application</p>
        </div>

        {/* Application Details Card */}
        <div className="card" style={{ marginBottom: '2rem' }}>
          <div className="card-header">
            <h3>Loan Application Details</h3>
          </div>
          <div className="card-body">
            <div className="details-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <label className="text-muted">Applicant</label>
                <p><strong>{request.applicant_name}</strong></p>
              </div>
              <div>
                <label className="text-muted">Application Reference</label>
                <p><strong>{request.application_reference}</strong></p>
              </div>
              <div>
                <label className="text-muted">Loan Product</label>
                <p><strong>{request.product_name}</strong></p>
              </div>
              <div>
                <label className="text-muted">Loan Amount</label>
                <p><strong>₦{Number(request.amount_requested).toLocaleString()}</strong></p>
              </div>
              <div>
                <label className="text-muted">Your Name</label>
                <p><strong>{request.guarantor_name}</strong></p>
              </div>
              <div>
                <label className="text-muted">Your Email</label>
                <p><strong>{request.guarantor_email}</strong></p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        {step !== 'success' && (
          <div className="progress-steps" style={{ display: 'flex', justifyContent: 'space-around', marginBottom: '2rem' }}>
            <div className={`progress-step ${step === 'info' ? 'active' : (step === 'documents' || step === 'review') ? 'completed' : ''}`}>
              <div className="step-circle">1</div>
              <p>Personal Info</p>
            </div>
            <div className={`progress-step ${step === 'documents' ? 'active' : step === 'review' ? 'completed' : ''}`}>
              <div className="step-circle">2</div>
              <p>Documents</p>
            </div>
            <div className={`progress-step ${step === 'review' ? 'active' : ''}`}>
              <div className="step-circle">3</div>
              <p>Review & Submit</p>
            </div>
          </div>
        )}

        {/* Error/Success Messages */}
        {error && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            <X size={20} />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
            <Check size={20} />
            <span>{success}</span>
          </div>
        )}

        {/* Step 1: Personal Information */}
        {step === 'info' && (
          <div className="card">
            <div className="card-header">
              <h3><User size={20} /> Your Information</h3>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmitInfo}>
                <div className="form-group">
                  <label>Residential Address *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Enter your full residential address"
                    required
                  />
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="City"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>State *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="State"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>ID Type *</label>
                  <select
                    className="form-control"
                    value={formData.idType}
                    onChange={(e) => setFormData({ ...formData, idType: e.target.value })}
                    required
                  >
                    <option value="">Select ID Type</option>
                    <option value="passport">International Passport</option>
                    <option value="national_id">National ID Card</option>
                    <option value="drivers_license">Driver's License</option>
                    <option value="government_id">Government Issued ID</option>
                  </select>
                </div>

                <div className="button-group" style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                  <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                    Continue to Documents
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Step 2: Document Upload */}
        {step === 'documents' && (
          <div className="card">
            <div className="card-header">
              <h3><Upload size={20} /> Upload Documents</h3>
            </div>
            <div className="card-body">
              <p className="text-muted" style={{ marginBottom: '1.5rem' }}>
                Please upload your ID document and a clear selfie photo for verification
              </p>

              {/* ID Document Upload */}
              <div className="document-upload-section" style={{ marginBottom: '2rem' }}>
                <h4><FileText size={18} /> ID Document</h4>
                <div className="upload-box" style={{
                  border: '2px dashed #ddd',
                  borderRadius: '8px',
                  padding: '2rem',
                  textAlign: 'center',
                  backgroundColor: idDocumentUploaded ? '#f0f9ff' : 'white'
                }}>
                  {idDocumentUploaded ? (
                    <div style={{ color: '#10b981' }}>
                      <Check size={48} style={{ marginBottom: '0.5rem' }} />
                      <p><strong>ID Document Uploaded</strong></p>
                      <p className="text-muted">You can upload again to replace</p>
                    </div>
                  ) : (
                    <div>
                      <Upload size={48} style={{ marginBottom: '0.5rem', color: '#999' }} />
                      <p>Upload your ID document</p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setIdDocument(file);
                        handleUploadDocument('id_document', file);
                      }
                    }}
                    style={{ marginTop: '1rem' }}
                  />
                </div>
              </div>

              {/* Selfie Upload */}
              <div className="document-upload-section" style={{ marginBottom: '2rem' }}>
                <h4><Camera size={18} /> Selfie Photo</h4>
                <div className="upload-box" style={{
                  border: '2px dashed #ddd',
                  borderRadius: '8px',
                  padding: '2rem',
                  textAlign: 'center',
                  backgroundColor: selfieUploaded ? '#f0f9ff' : 'white'
                }}>
                  {selfieUploaded ? (
                    <div style={{ color: '#10b981' }}>
                      <Check size={48} style={{ marginBottom: '0.5rem' }} />
                      <p><strong>Selfie Uploaded</strong></p>
                      <p className="text-muted">You can upload again to replace</p>
                    </div>
                  ) : (
                    <div>
                      <Camera size={48} style={{ marginBottom: '0.5rem', color: '#999' }} />
                      <p>Upload a clear selfie photo</p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setSelfie(file);
                        handleUploadDocument('selfie', file);
                      }
                    }}
                    style={{ marginTop: '1rem' }}
                  />
                </div>
              </div>

              <div className="button-group" style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setStep('info')}
                  style={{ flex: 1 }}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setStep('review')}
                  disabled={!idDocumentUploaded || !selfieUploaded}
                  style={{ flex: 1 }}
                >
                  Review & Submit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Review & Submit */}
        {step === 'review' && (
          <div className="card">
            <div className="card-header">
              <h3><Check size={20} /> Review & Submit</h3>
            </div>
            <div className="card-body">
              <p className="text-muted" style={{ marginBottom: '1.5rem' }}>
                Please review your information before final submission
              </p>

              <div className="review-section" style={{ marginBottom: '2rem' }}>
                <h4>Personal Information</h4>
                <div className="details-grid" style={{ display: 'grid', gap: '1rem', marginTop: '1rem' }}>
                  <div>
                    <label className="text-muted">Address</label>
                    <p>{formData.address}</p>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label className="text-muted">City</label>
                      <p>{formData.city}</p>
                    </div>
                    <div>
                      <label className="text-muted">State</label>
                      <p>{formData.state}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-muted">ID Type</label>
                    <p>{formData.idType}</p>
                  </div>
                </div>
              </div>

              <div className="review-section" style={{ marginBottom: '2rem' }}>
                <h4>Documents Uploaded</h4>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '4px'
                  }}>
                    <Check size={18} style={{ color: '#10b981' }} />
                    <span>ID Document</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '4px'
                  }}>
                    <Check size={18} style={{ color: '#10b981' }} />
                    <span>Selfie Photo</span>
                  </div>
                </div>
              </div>

              <div className="alert alert-info" style={{ marginBottom: '1.5rem' }}>
                <p><strong>Important:</strong> By submitting this form, you confirm that you agree to act as a guarantor for this loan application and that all information provided is accurate.</p>
              </div>

              <div className="button-group" style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setStep('documents')}
                  style={{ flex: 1 }}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleFinalSubmit}
                  style={{ flex: 1 }}
                >
                  Submit Authorization
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success State */}
        {step === 'success' && (
          <div className="card" style={{ textAlign: 'center' }}>
            <div className="card-body" style={{ padding: '3rem 2rem' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <Check size={48} style={{ color: 'white' }} />
              </div>
              <h2 style={{ marginBottom: '1rem' }}>Authorization Complete!</h2>
              <p className="text-muted" style={{ fontSize: '1.1rem', marginBottom: '2rem' }}>
                Thank you for completing the guarantor authorization process.
              </p>
              <p>
                Your information has been successfully submitted. The applicant <strong>{request.applicant_name}</strong> has been notified.
              </p>
              <p className="text-muted" style={{ marginTop: '1rem' }}>
                You will receive email updates about the loan application status.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuarantorAuthorization;
